import * as React from 'react';
import { Form } from 'react-final-form';
import { useCallback, useEffect, useState } from 'react';
import { AuthApi, ROUTE_LOGIN, useAppConfig } from '@mc/react-app-base';
import { getLoadDataError, LoadDataApiError } from '@mc/axios-api';
import {
  Block,
  Button,
  ButtonToolbar,
  Callout,
  Card,
  Dialog,
  DialogBody,
  DialogFooter,
  Spinner,
  VF,
} from '@mc/ui-v4';
import { AuthCredentials } from '@mc/react-auth';
import { AuthData } from '@mc/react-auth';
import { AppErrorPage } from '../pages/AppErrorPage';
import { rffFormSubmit } from '../errors/errors';
import { ApiError } from '@mc/axios-api';
import { useAuth } from '@mc/react-auth';

export enum AlternativeLoginMethods {
  OPENID = 'OPENID',
  NTLM = 'NTLM',
}

type Props = {
  /**
   * React redirect, např. /stanoviska/sablony
   */
  redirect?: string;
  /**
   * Server redirect, např. http://localhost:9670/stanoviska/sablony
   */
  serverRedirect?: string;
  alternativeLoginMethods?: AlternativeLoginMethods[];
  appTitle?: string;
  onLoginSuccess?: (authData: AuthData) => void;
};

const PROFILE_MARBES = 'marbes';

type LoginFormData = Omit<AuthCredentials, 'persistent'>;

export const LoginForm: React.FC<Props> = ({
  redirect,
  serverRedirect,
  alternativeLoginMethods,
  onLoginSuccess,
  appTitle,
}) => {
  const [useNtml, setNtlm] = useState<boolean | undefined>(undefined);
  const [useOidc, setOidc] = useState<boolean | undefined>(undefined);
  const [formDisabled, setDisabled] = useState(false);
  const [fetchDataError, setFetchDataError] = useState<
    LoadDataApiError | undefined
  >(undefined);
  const [loginDataError, setLoginDataError] = useState<
    LoadDataApiError | undefined
  >(undefined);

  const auth = useAuth();

  let isPasswordRequired = true;
  if (auth.data) {
    isPasswordRequired = auth.data.appProfiles.indexOf(PROFILE_MARBES) === -1;
  }

  const handleLogin = useCallback(
    (authData: AuthData) => {
      auth.login(authData);
      onLoginSuccess?.(authData);
    },
    [redirect, onLoginSuccess, auth]
  );

  useEffect(() => {
    if (alternativeLoginMethods!.includes(AlternativeLoginMethods.NTLM)) {
      AuthApi.canUseNtlm()
        .then((res) => setNtlm(res.data))
        .catch((err) => {
          let dataError = getLoadDataError(err);
          setFetchDataError(dataError);
        });
    }

    if (alternativeLoginMethods!.includes(AlternativeLoginMethods.OPENID)) {
      AuthApi.isOpenIdConfigured()
        .then((res) => setOidc(res.data))
        .catch((err) => {
          let dataError = getLoadDataError(err);
          setFetchDataError(dataError);
        });
    }
  }, []);

  const appConfig = useAppConfig();

  /**
   * window.location.href bey /login
   */
  const loginByNtlm = async () => {
    try {
      setDisabled(true);
      setLoginDataError(undefined);

      // U ntlm chceme použít předávaný redirect serverRedirect a pokud není, tak dáme redirect na home (url bez /login, tedy urlWithoutLogin)
      const urlWithoutLogin = window.location.href.replace(ROUTE_LOGIN, '');
      const ntlmUrl =
        appConfig.serverContextPath +
        AuthApi.ntlmUrl(serverRedirect || urlWithoutLogin);
      window.location.href = ntlmUrl;
    } catch (err) {
      let loadDataError = getLoadDataError(err);
      setLoginDataError(loadDataError);
    } finally {
      setDisabled(false);
    }
  };

  // TODO není funkční
  const loginByOidc = async () => {
    try {
      setDisabled(true);
      setLoginDataError(undefined);

      // TODO není funkční
      // await AuthApi.loginByOpenId();
    } catch (err) {
      let loadDataError = getLoadDataError(err);
      setLoginDataError(loadDataError);
    } finally {
      setDisabled(false);
    }
  };

  if (fetchDataError) {
    return (
      <AppErrorPage
        message={fetchDataError.message}
        description={fetchDataError.description}
      />
    );
  }

  if (useNtml === undefined || useOidc === undefined) {
    return <Spinner />;
  }

  return (
    <Form<LoginFormData>
      mutators={{
        clearSubmitError,
      }}
      validate={(data) => {
        const errors: any = {};

        if (!data.username) {
          errors.username = 'Uživatelské jméno je povinné';
        }
        if (isPasswordRequired) {
          if (!data.password) {
            errors.password = 'Heslo je povinné';
          }
        }

        return errors;
      }}
      onSubmit={async (data) => {
        setLoginDataError(undefined);

        return rffFormSubmit(async () => {
          const resData = (await AuthApi.login(data)).data;

          // Chyba přihlášení se vrací ze serveru jinak, budeme simulovat jako chybu formuláře - submitu
          if (resData.error) {
            const err: ApiError = {
              messageTitle: resData.error,
              cancelled: false,
            };
            throw err;
          }

          handleLogin(resData);
        }, true);
      }}
    >
      {(formRenderProps) => {
        return (
          <Dialog inline title="Přihlášení" extra={appTitle}>
            <form onSubmit={formRenderProps.handleSubmit}>
              <DialogBody>
                {loginDataError && (
                  <Callout intent={'danger'} className={'mb-1'}>
                    {loginDataError.message}
                    {loginDataError.description && (
                      <div>
                        <small>{loginDataError.description}</small>
                      </div>
                    )}
                  </Callout>
                )}
                {formRenderProps.submitError && (
                  <Callout intent={'danger'} className={'mb-1'}>
                    {formRenderProps.submitError}
                  </Callout>
                )}
                <Card mode={'edit'}>
                  <Block>
                    <VF.String
                      required
                      label={'Uživatelské jméno'}
                      name={'username'}
                      autocomplete={'username'}
                      disabled={formDisabled || formRenderProps.submitting}
                    />
                    {isPasswordRequired && (
                      <VF.String
                        required
                        type={'password'}
                        label={'Heslo'}
                        name={'password'}
                        disabled={formDisabled || formRenderProps.submitting}
                      />
                    )}
                  </Block>
                </Card>
              </DialogBody>
              <DialogFooter
                extra={
                  <ButtonToolbar>
                    {useNtml && (
                      <Button
                        onClick={async () => {
                          formRenderProps.form.mutators.clearSubmitError();
                          await loginByNtlm();
                        }}
                        disabled={formDisabled || formRenderProps.submitting}
                      >
                        Přihlásit pomocí Windows
                      </Button>
                    )}
                    {useOidc && (
                      <Button
                        onClick={async () => {
                          formRenderProps.form.mutators.clearSubmitError();
                          await loginByOidc();
                        }}
                        disabled={formDisabled || formRenderProps.submitting}
                      >
                        Přihlásit pomocí OpenID
                      </Button>
                    )}
                  </ButtonToolbar>
                }
              >
                <Button
                  type={'submit'}
                  style={'primary'}
                  disabled={formDisabled || formRenderProps.submitting}
                >
                  Přihlásit
                </Button>
              </DialogFooter>
            </form>
          </Dialog>
        );
      }}
    </Form>
  );
};

LoginForm.defaultProps = {
  alternativeLoginMethods: Object.values(AlternativeLoginMethods),
};

const clearSubmitError = ([], state: any, tools: any) => {
  let newState: any = state;

  // Submit errors
  newState = tools.setIn(newState, 'formState.submitError', undefined) as any;

  // Promítnutí do state
  state.formState = newState.formState;
  state.fields = newState.fields;
};
